/**
 * FormControlPhone — Ukraine (+380) only, Formik integration.
 */

import React from 'react';
import { FormControl, FormLabel } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import FormErrorMessage from './FormErrorMessage';
import { FlagComponent } from './FlagComponent';
import { useAllowedCountries, usePhoneMaxLength, usePhoneCountryLabels } from './hooks';
import { DEFAULT_CONFIG, getPhoneInputStyles, getCountrySelectStyles } from './constants';
import { customPhoneMetadata } from './phoneMetadata';
import './styles.scss';

export { normalizePhoneForSubmit, formatPhoneE123, formatPhoneNational } from './phoneHelper';

export interface IFormControlPhone {
  name: string;
  label?: string;
  form?: FormikProps<any>;
  isRequired?: boolean;
  isSubmitted?: boolean;
  width?: string;
  height?: string;
  defaultCountry?: string;
  placeholder?: string;
  className?: string;
  LabelComponent?: React.ComponentType<any>;
  disabled?: boolean;
  isDisabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const FormControlPhone: React.FC<IFormControlPhone> = (props) => {
  const {
    name,
    label,
    form,
    isRequired: explicitIsRequired,
    isSubmitted = false,
    width = DEFAULT_CONFIG.width,
    height = DEFAULT_CONFIG.height,
    defaultCountry: _defaultCountry = DEFAULT_CONFIG.defaultCountry,
    placeholder,
    className = '',
    LabelComponent,
    disabled,
    isDisabled,
    onKeyDown: externalOnKeyDown,
  } = props;

  const isFieldDisabled = disabled || isDisabled || false;
  const { i18n, t } = useTranslation();

  const phoneValue = (form?.values?.[name] as string | undefined) ?? '';
  const phoneError = form?.errors?.[name] as string | undefined;

  const { countries, finalDefaultCountry } = useAllowedCountries();

  const { handleKeyDown: handleKeyDownWithValidation } = usePhoneMaxLength({
    phoneValue,
    defaultCountry: finalDefaultCountry,
  });

  const { labels: countryLabels } = usePhoneCountryLabels({ language: i18n.language });

  const isInvalid = !!phoneError && isSubmitted;

  const isRequired =
    explicitIsRequired !== undefined
      ? explicitIsRequired
      : typeof (form as any)?.isRequired === 'function'
      ? (form as any).isRequired(name)
      : false;

  const handlePhoneChange = (value: string | undefined) => {
    form?.setFieldValue(name, value ?? '');
  };

  const handleBlur = () => {
    form?.setFieldTouched(name, true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    handleKeyDownWithValidation(e, externalOnKeyDown);
  };

  return (
    <FormControl
      isRequired={isRequired}
      className={`form-control-phone-split ${className}`}
      isInvalid={isInvalid}
      width={width}
    >
      {label && (
        <FormLabel htmlFor={name} fontSize='inherit'>
          {LabelComponent ? <LabelComponent text={label} /> : t(label)}
        </FormLabel>
      )}

      <div className='phone-input-wrapper ltr' dir='ltr'>
        <PhoneInput
          international
          withCountryCallingCode
          countryCallingCodeEditable={false}
          defaultCountry={finalDefaultCountry as any}
          countries={countries as any}
          labels={countryLabels}
          metadata={customPhoneMetadata as any}
          value={String(phoneValue)}
          onChange={handlePhoneChange}
          onBlur={handleBlur}
          disabled={isFieldDisabled}
          placeholder={placeholder || t('placeholder.phone', '+380 XX XXX XX XX')}
          className={`phone-input-container ${isInvalid ? 'is-invalid' : ''}`}
          flagComponent={FlagComponent}
          addInternationalOption={false}
          smartCaret
          numberInputProps={{
            className: 'phone-number-input',
            dir: 'ltr',
            onKeyDown: handleKeyDown,
            style: getPhoneInputStyles(height, isInvalid),
          }}
          countrySelectProps={{
            className: 'country-select',
            style: getCountrySelectStyles(height, isInvalid),
          }}
        />
      </div>

      {phoneError && isSubmitted && <FormErrorMessage text={t(phoneError)} />}
    </FormControl>
  );
};

export default FormControlPhone;
